import { PkButton, PkDivider, PkIcon } from '@uefadigital/panenka-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';

import * as style from './style.module.css';

export interface NearlyDoneModalInterface {
  isOpen: boolean;
  setClose: () => void;
  competitionShortName: string;
  competitionId: string;
  teamId: Array<string>;
}

export const NearlyDoneModal = ({
  isOpen,
  setClose,
  competitionShortName,
  competitionId,
  teamId,
}: NearlyDoneModalInterface) => {
  const { t } = useTranslation();
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const inputElement = useRef<HTMLInputElement>(null);

  const getLanguage = () => {
    let shortLang: string = window.navigator.languages ? window.navigator.languages[0] : 'GB';

    if (shortLang.indexOf('-') !== -1) {
      shortLang = shortLang.split('-')[0];
    }

    if (shortLang.indexOf('_') !== -1) {
      shortLang = shortLang.split('_')[0];
    }

    if (shortLang.toUpperCase() === 'EN') {
      return 'GB';
    }

    return shortLang.toUpperCase();
  };

  const link = `${
    import.meta.env.VITE_CALENDAR_API_BASE_URL
  }/v1/calendar.ics?teamId=${teamId}&competitionId=${competitionId}&language=EN&reminder=60&countryCode=${getLanguage()}`;
  const webcalUrl = `webcal://${link}`;
  const httpUrl = `https://${link}`;

  const modalStyle = (isHelpModal: boolean): Modal.Styles => {
    return {
      overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgb(0 0 0 / 50%)',
        zIndex: 9999,
      },
      content: {
        position: 'relative',
        top: '40px',
        inset: '0px',
        bottom: '40px',
        boxShadow: '0 3px 9px rgb(0 0 0 / 50%)',
        background: '#fff',
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch',
        borderRadius: '4px',
        outline: 'none',
        maxWidth: isHelpModal ? '600px' : '1392px',
        maxHeight: '600px',
        margin: 'auto',
        marginTop: '40px',
        padding: '0px',
      },
    };
  };

  const modalRow = (number: string, value: string) => {
    return (
      <li className={`pk-mt--s ${style.lineHeight30}`}>
        <span
          className={`pk-text--ui-01 pk-border--primary-01 pk-bg--primary-01 pk-d--inline-block pk-mr--xs2 pk-text--center pk-font-size--s ${style.roundedCorners}`}
        >
          {number}
        </span>
        <span className="pk-font-size--s">{value}</span>
      </li>
    );
  };

  const onClose = () => {
    setIsHelpModalOpen(false);
    setIsCopied(false);
    setClose();
  };

  const handleCopyClick = () => {
    if ('clipboard' in navigator) {
      navigator.clipboard.writeText(webcalUrl);
    } else {
      document.addEventListener('copy', (event) => {
        event?.clipboardData?.setData('text/plain', webcalUrl);
        event.preventDefault();
      });
      document.execCommand('copy');
    }
    setIsCopied(true);
  };

  return (
    <div>
      <Modal
        isOpen={isOpen}
        appElement={document.getElementById('app') as HTMLElement}
        onRequestClose={onClose}
        closeTimeoutMS={250}
        className={{
          base: style.reactModalOverlay,
          afterOpen: style.afterOpen,
          beforeClose: style.beforeClose,
        }}
        style={modalStyle(false)}
        bodyOpenClassName={style.reactModalOpen}
        contentLabel={t('add_to_calendar')}
      >
        <div className="pk-container" pk-competition={competitionShortName} pk-theme="light">
          <div className="pk-col pk-col--span-12 pk-p--xs pk-pb--xs pk-text--primary-01 pk-d--flex pk-justify-content--between">
            <div className="pk-font-size--l pk-font-weight--bold pk-line-height--sd pk-font-style--normal pk-text--text-0">
              {t('nearly_done')}
            </div>
            <PkButton appearance="text" onPkClick={onClose} className="pk-pr--s">
              <PkIcon name="close-web-icon" class="pk-text--text-01" size={15} />
            </PkButton>
          </div>
          <div className="pk-col pk-col--span-5">
            <div className="pk-justify-content--center pk-d--flex">
              <PkButton appearance="outlined" link-button="true" link-href={httpUrl} link-target="_blank" width="220">
                {t('install_in_your_calendar')}
              </PkButton>
            </div>
            <div className="pk-mt--xl pk-font-size--s pk-text--center">{t('install_description')}</div>
          </div>
          <div className="pk-col pk-col--span-2 pk-justify-content--center pk-d--flex">
            <span className="pk-position--absolute pk-text--text-01 pk-font-size--m pk-p--s pk-mt--l pk-top--50 pk-left--50 pk-bg--ui-01 pk-translate-middle">
              {t('or')}
            </span>
            <PkDivider class="divider--vertical pk-px--auto" direction="vertical" />
          </div>
          <div className="pk-col pk-col--span-5">
            <div className="pk-justify-content--center pk-d--flex pk-text--text-01">
              <PkButton
                className={`${isCopied ? 'pk-d--none' : 'pk-mb--xl'}`}
                appearance="outlined"
                onPkClick={handleCopyClick}
              >
                {t('copy_link')}
              </PkButton>
              <div className={isCopied ? '' : 'pk-d--none'}>
                <div
                  className="pk-mb--xs pk-font-size--s pk-text--center pk-font-weight--bolder pk-line-height--sd pk-font-style--normal"
                  style={{ display: 'block' }}
                >
                  {t('link_copied_to_clipboard')}
                </div>
                <input
                  ref={inputElement}
                  className={`${style.inputWebcal} pk-p--m pk-m--xs pk-font-size--s`}
                  type="text"
                  value={webcalUrl}
                />
              </div>
            </div>
            <div className="pk-font-size--s pk-text--center">
              {t('copy_description')}
              <PkButton
                appearance="text"
                type="button"
                className="pk-pl--xs2 pk-font-size--m"
                onPkClick={() => setIsHelpModalOpen(true)}
              >
                {t('how_i_can_do_this')}
              </PkButton>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isHelpModalOpen}
        appElement={document.getElementById('app') as HTMLElement}
        onRequestClose={() => {
          setIsHelpModalOpen(false);
        }}
        closeTimeoutMS={250}
        className={{
          base: style.reactModalOverlay,
          afterOpen: style.afterOpen,
          beforeClose: style.beforeClose,
        }}
        style={modalStyle(true)}
        bodyOpenClassName={style.reactModalOpen}
        shouldCloseOnOverlayClick
        contentLabel={t('add_to_calendar')}
      >
        <div className="pk-m--0 pk-p--0 pk-text--primary-01" pk-competition={competitionShortName} pk-theme="light">
          <div className="pk-d--flex pk-justify-content--between pk-align-items--center pk-p--m">
            <div className="pk-font-size--m pk-font-weight--bold pk-line-height--sd pk-font-style--normal pk-text--text-0">
              {t('how_to_add_a_calendar')}
            </div>

            <PkButton
              appearance="text"
              onPkClick={() => {
                setIsHelpModalOpen(false);
              }}
              className="pk-pr--l"
            >
              <PkIcon name="close-web-icon" class="pk-text--text-01" size={15} />
            </PkButton>
          </div>
          <div className="pk-pr--12 pk-justify-content--center pk-d--flex">
            <PkDivider class="divider--horizontal pk-px--auto" direction="horizontal" />
          </div>
          <div className="pk-mt--l pk-pl--xl2 pk-px--m">
            <div>
              <span className="pk-font-weight--bolder pk-pb--s">{t('how_to_add_block.outlook.title')}</span>
              <ul>
                {modalRow('1', t('how_to_add_block.outlook.points.1'))}
                {modalRow('2', t('how_to_add_block.outlook.points.2'))}
                {modalRow('3', t('how_to_add_block.outlook.points.3'))}
                {modalRow('4', t('how_to_add_block.outlook.points.4'))}
              </ul>
            </div>
          </div>
          <div className="pk-mt--l pk-pl--xl2 pk-px--m">
            <div>
              <span className="pk-font-weight--bolder pk-pb--s">{t('how_to_add_block.google.title')}</span>
              <ul>
                {modalRow('1', t('how_to_add_block.google.points.1'))}
                {modalRow('2', t('how_to_add_block.google.points.2'))}
                {modalRow('3', t('how_to_add_block.google.points.3'))}
              </ul>
            </div>
          </div>
          <div className="pk-mt--l">
            <div className="pk-pl--xs pk-font-size--12">{t('how_to_add_similar')}</div>
          </div>

          <div className="pk-p--xs pk-pb--s">
            <div className="pk-justify-content--center pk-d--flex">
              <PkButton
                appearance="outlined"
                onPkClick={() => {
                  setIsHelpModalOpen(false);
                }}
              >
                {t('ok_got_it')}
              </PkButton>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};
